/*
 * On desktop the product thumbnail rail is laid out as a vertical column to the
 * left of the main image (see section-main-product.css). The base
 * SliderComponent only knows how to scroll horizontally, so this enhancement
 * drives the vertical rail:
 *
 *   - Arrows appear only when the thumbnails don't all fit (the rail can
 *     scroll): the up arrow shows while not scrolled to the top and the down
 *     arrow shows while not scrolled to the bottom.
 *   - Clicking an arrow scrolls the rail vertically.
 */
(function () {
  const desktop = window.matchMedia('(min-width: 990px)');
  // Between these two the thumbnails are a horizontal strip paged by arrows.
  // Below tabletUp it's swiped, and the arrows stay hidden.
  const tabletUp = window.matchMedia('(min-width: 750px)');
  const MAX_VISIBLE = 5;
  // Thumbnails fully visible beside the main image before the rail scrolls,
  // and the fewest the rail will size itself for.
  const FULL_ROWS = 4;
  const MIN_ROWS = 3;
  const GAP = 8;
  const SLIDES_PER_CLICK = 3;
  const EDGE_TOLERANCE = 2;

  function enhance(rail) {
    if (rail.dataset.verticalEnhanced) return;
    const list = rail.querySelector('.thumbnail-list');
    const prevButton = rail.querySelector('button[name="previous"]');
    const nextButton = rail.querySelector('button[name="next"]');
    if (!list || !prevButton || !nextButton) return;
    rail.dataset.verticalEnhanced = 'true';

    const product = rail.closest('.product');
    const gallery = rail.closest('media-gallery');

    // The collection page widens its media column to hold the rail, so the main
    // image keeps the width it had before the rail existed and the thumbnails
    // are free to be sized to its height (see --media-column-fraction in
    // section-main-product.css). Everywhere else the rail keeps its fixed CSS
    // width and the image gives up the space.
    const mediaColumnFraction = () =>
      product === null
        ? NaN
        : parseFloat(
            getComputedStyle(product).getPropertyValue(
              '--media-column-fraction'
            )
          );

    const widensMediaColumn = () => mediaColumnFraction() > 0;

    const mainImage = () =>
      gallery
        ? gallery.querySelector('slider-component:not(.thumbnail-slider)')
        : null;

    const visibleThumbnails = () =>
      Array.from(list.querySelectorAll('.thumbnail-list__item')).filter(
        (thumb) => thumb.getBoundingClientRect().height > 0
      );

    // Same predicate, but stops at the first match — capHeight() runs on every
    // scroll frame, so it must not measure every thumbnail to find one height.
    const firstVisibleThumbnail = () =>
      Array.from(list.querySelectorAll('.thumbnail-list__item')).find(
        (thumb) => thumb.getBoundingClientRect().height > 0
      );

    /*
     * Size the thumbnails so the column spans the main image's height:
     *
     *   rows * size + gaps * GAP = imageHeight
     *
     * Three or four thumbnails fill that height between them. Beyond four the
     * rail shows four and half of the next, so it reads as continuing past the
     * image's bottom edge the way the PDP's does. Fewer than three keep the
     * three-row size rather than growing to half the image — the rail just ends
     * short.
     */
    const sizeRailToImageHeight = () => {
      const viewer = mainImage();
      if (!product || !viewer) return;
      if (!desktop.matches || !widensMediaColumn()) {
        product.style.removeProperty('--thumbnail-rail-width');
        return;
      }
      const count = visibleThumbnails().length;
      const imageHeight = viewer.getBoundingClientRect().height;
      if (count < 1 || imageHeight <= 0) return;

      const rows = Math.min(Math.max(count, MIN_ROWS), FULL_ROWS + 0.5);
      const size =
        Math.round(((imageHeight - (Math.ceil(rows) - 1) * GAP) / rows) * 100) /
        100;
      if (!(size > 0)) return;
      const value = `${size}px`;
      if (product.style.getPropertyValue('--thumbnail-rail-width') !== value) {
        product.style.setProperty('--thumbnail-rail-width', value);
      }
    };

    const scrollByStep = (direction) => {
      const item = list.querySelector('.thumbnail-list__item');
      const itemHeight = item ? item.getBoundingClientRect().height + GAP : 100;
      list.scrollBy({ top: direction * itemHeight * SLIDES_PER_CLICK, behavior: 'smooth' });
    };

    // The base SliderComponent disables the arrows using horizontal metrics
    // that never apply here — keep them enabled on desktop so the shown ones
    // stay clickable, and manage visibility ourselves.
    const keepEnabled = () => {
      if (!desktop.matches) return;
      prevButton.removeAttribute('disabled');
      nextButton.removeAttribute('disabled');
    };
    [prevButton, nextButton].forEach((button) => {
      new MutationObserver(keepEnabled).observe(button, {
        attributes: true,
        attributeFilter: ['disabled'],
      });
    });

    // Cap the visible area to MAX_VISIBLE thumbnails, measured from the real
    // (square) thumbnail height so it tracks the responsive layout exactly.
    // Measure the first *visible* thumbnail — with variant image-grouping the
    // first item in the DOM can be hidden (height 0).
    //
    // Where the media column was widened for the rail, cap to the main image's
    // height instead, so the rail ends level with the image — four thumbnails
    // in full and the next one sliced off, the way the PDP reads.
    const capHeight = () => {
      if (!desktop.matches) {
        list.style.maxHeight = '';
        return;
      }
      let maxHeight = 0;
      const viewer = mainImage();
      if (widensMediaColumn() && viewer) {
        // max-height is a border-box measure, so add the shadow room the list
        // carries as padding — otherwise it eats into the thumbnails' share and
        // clips the last one. The list is pulled back by the same amount with a
        // negative margin, so the thumbnails still span exactly the image.
        const style = getComputedStyle(list);
        const padY =
          (parseFloat(style.paddingTop) || 0) +
          (parseFloat(style.paddingBottom) || 0);
        maxHeight = viewer.getBoundingClientRect().height + padY;
      } else {
        const item = firstVisibleThumbnail();
        const itemHeight = item ? item.getBoundingClientRect().height : 0;
        if (itemHeight <= 0) return;
        maxHeight = MAX_VISIBLE * itemHeight + (MAX_VISIBLE - 1) * GAP;
      }
      if (maxHeight <= 0) return;
      const value = `${Math.round(maxHeight * 100) / 100}px`;
      if (list.style.maxHeight !== value) list.style.maxHeight = value;
    };

    // product-media-gallery.liquid hides both arrows with .large-up-hide
    // (display: none !important) whenever there are five media or fewer. The
    // rail can still overflow at five — it shows four and a half — so the
    // priority has to be matched to reveal them.
    const showArrow = (button, visible) =>
      button.style.setProperty(
        'display',
        visible ? 'flex' : 'none',
        'important'
      );

    // Hand the arrows back to the stylesheet. Only safe where the stylesheet
    // actually has an opinion: .slider-button is display:flex by default and
    // the theme's only override for it lives inside @media (min-width: 750px),
    // so releasing below that would leave two arrows on a phone whenever the
    // markup didn't hide them itself (it only does so at five media or fewer).
    const releaseArrows = () => {
      prevButton.style.removeProperty('display');
      nextButton.style.removeProperty('display');
    };

    // Between 750px and the rail breakpoint the stylesheet alone can't get the
    // strip right: the arrows are marked .large-up-hide whenever there are five
    // media or fewer, which assumes five always fit. Only four do at tablet
    // width, so a five-media strip scrolls with no way to page it. Show them
    // from real overflow instead, and leave the base slider to disable them at
    // the ends. Below 750px the strip is swiped, so leave Dawn's own hiding
    // alone rather than forcing arrows onto a phone.
    const syncHorizontalArrows = () => {
      const overflows = list.scrollWidth > list.clientWidth + EDGE_TOLERANCE;
      if (!overflows) {
        releaseArrows();
        return;
      }
      showArrow(prevButton, true);
      showArrow(nextButton, true);
    };

    let scheduled = false;
    const refresh = () => {
      scheduled = false;
      sizeRailToImageHeight();
      capHeight();

      if (!desktop.matches) {
        if (tabletUp.matches) syncHorizontalArrows();
        else {
          // Swiped, not paged — keep the arrows off the strip regardless of how
          // many media there are.
          showArrow(prevButton, false);
          showArrow(nextButton, false);
        }
        return;
      }

      // The rail only scrolls when its thumbnails overflow the visible cap.
      // If they all fit, no arrows are needed.
      const canScroll = list.scrollHeight > list.clientHeight + EDGE_TOLERANCE;
      if (!canScroll) {
        showArrow(prevButton, false);
        showArrow(nextButton, false);
        return;
      }

      keepEnabled();
      const atTop = list.scrollTop <= EDGE_TOLERANCE;
      const atBottom =
        list.scrollTop + list.clientHeight >= list.scrollHeight - EDGE_TOLERANCE;
      showArrow(prevButton, !atTop);
      showArrow(nextButton, !atBottom);
    };

    const scheduleRefresh = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(refresh);
    };

    const handleClick = (direction) => (event) => {
      if (!desktop.matches) return; // let the base horizontal behavior run on mobile
      event.stopImmediatePropagation();
      event.preventDefault();
      scrollByStep(direction);
    };
    prevButton.addEventListener('click', handleClick(-1), true);
    nextButton.addEventListener('click', handleClick(1), true);

    list.addEventListener('scroll', scheduleRefresh, { passive: true });
    window.addEventListener('resize', scheduleRefresh);
    window.addEventListener('load', scheduleRefresh);
    desktop.addEventListener('change', scheduleRefresh);
    tabletUp.addEventListener('change', scheduleRefresh);

    // Thumbnails are lazy-loaded, so the rail only starts overflowing once the
    // images have real dimensions — re-evaluate on image load and rail resize.
    if (gallery) gallery.addEventListener('load', scheduleRefresh, true);
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(scheduleRefresh);
      resizeObserver.observe(rail);
      resizeObserver.observe(list);
      const viewer = mainImage();
      if (viewer) resizeObserver.observe(viewer);
    }
    // Recount when variant grouping toggles which thumbnails are shown.
    new MutationObserver(scheduleRefresh).observe(list, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    refresh();
  }

  function run() {
    document.querySelectorAll('media-gallery .thumbnail-slider').forEach(enhance);
  }

  if (document.readyState !== 'loading') run();
  else document.addEventListener('DOMContentLoaded', run);
  desktop.addEventListener('change', run);
})();
